import React, { useState, useRef } from "react";
import Slider from "react-slick";
import { Box, IconButton } from "@mui/material";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

interface ImageSliderProps {
  images: any;
}

const ImageSlider: React.FC<ImageSliderProps> = ({ images }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const sliderRef = useRef<any>(null);

  const NextArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        right: "15px",
        zIndex: 2,
        transform: "translateY(-50%)",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        color: "white",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          color: "black",
        },
        width: "40px",
        height: "40px",
      }}
    >
      <NavigateNextIcon fontSize="medium" />
    </IconButton>
  );

  const PrevArrow = ({ onClick }: { onClick?: () => void }) => (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        top: "50%",
        left: "15px",
        zIndex: 2,
        transform: "translateY(-50%)",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        color: "white",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          color: "black",
        },
        width: "40px",
        height: "40px",
      }}
    >
      <NavigateBeforeIcon fontSize="medium" />
    </IconButton>
  );

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (index: number) => setCurrentSlide(index),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentSlide(index);
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const slides = images.map((src: string) => ({ src }));

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {/* Main Image Container */}
      <Box
        sx={{
          padding: "0px",
          borderRadius: "8px",
          position: "relative",
          "&:hover .fullscreen-btn": { opacity: 1 },
        }}
      >
        <Slider ref={sliderRef} {...settings}>
          {images.map((img: string, index: number) => (
            <Box
              key={index}
              sx={{ position: "relative", cursor: "zoom-in" }}
              onClick={() => openLightbox(index)}
            >
              <Box
                component="img"
                src={img}
                alt={`Image ${index}`}
                sx={{
                  width: "100%",
                  height: "300px",
                  borderRadius: "8px",
                  objectFit: {
                    xs: "cover",
                    sm: "contain",
                  },
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Slider>

        {/* Fullscreen icon — desktop hover, always visible on mobile */}
        <IconButton
          className="fullscreen-btn"
          onClick={() => openLightbox(currentSlide)}
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 3,
            backgroundColor: "rgba(0,0,0,0.55)",
            color: "white",
            width: "36px",
            height: "36px",
            opacity: { xs: 1, md: 0 },
            transition: "opacity 0.2s ease",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
          }}
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Thumbnails */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "flex-start",
          marginTop: "10px",
          gap: "8px",
          overflowX: "scroll",
          maxWidth: "100%",
          paddingBottom: "10px",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {images.map((img: string, index: number) => (
          <Box
            key={index}
            component="img"
            src={img}
            alt={`Thumbnail ${index}`}
            onClick={() => handleThumbnailClick(index)}
            sx={{
              width: "60px",
              height: "60px",
              borderRadius: "4px",
              objectFit: "cover",
              cursor: "pointer",
              border: index === currentSlide ? "2px solid #1976d2" : "2px solid #ccc",
              opacity: index === currentSlide ? 1 : 0.5,
              transition: "opacity 0.3s ease, border 0.3s ease",
              flexShrink: 0,
            }}
          />
        ))}
      </Box>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={slides}
        index={lightboxIndex}
        on={{ view: ({ index }) => setLightboxIndex(index) }}
        styles={{
          container: { backgroundColor: "rgba(0,0,0,0.95)" },
          slide: { padding: "24px" },
        }}
        carousel={{ finite: false }}
      />
    </Box>
  );
};

export default ImageSlider;
